import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  Alert,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  useMediaQuery,
} from "@mui/material"
import { SkipassSolid } from "@skistarab/icons"
import theme from "@skistarab/mui-theme"
import { useQueryClient } from "@tanstack/react-query"
import { addMonths, format } from "date-fns"
import { useTranslation } from "react-i18next"

import {
  DateSelector,
  PageContainer,
  PassDurationSelector,
  PriceSummeryBar,
  SrOnly,
  StyledHelperText,
  SummaryGrid,
  SummaryRow,
} from "@/components"
import { ConfirmNavigationDialog } from "@/components/ConfirmNavigationDialog"
import { FyiWidget, PriceSummaryDrawer } from "@/features"
import { useDataLayer } from "@/features/DataLayer"
import {
  useDateConstraints,
  useIsSummer,
  usePrerenderSignal,
  useSelectedBookingLength,
  useSelectedResort,
} from "@/hooks"
import {
  useBookingLengthParam,
  useCategoryParam,
  useDateParam,
  useResortParam,
} from "@/hooks/useSkipassFlow"
import {
  useCartContextQuery,
  useCartUsersQuery,
  useProducts,
  useUnavailableDatesPerCategoryQuery,
  useWebCategories,
} from "@/queries/hooks"
import { unavailableDatesPerCategoryQueryOptions } from "@/queries/hooks/useUnavailableDatesPerCategoryQuery"
import { BookingLength } from "@/queries/models"
import { useSkipassStore } from "@/store/skipassStore"

import ProductList from "./components/ProductList"
import { useProductStatus, useConfirmBeforeAction } from "./hooks"
import { useCartActions } from "../PassHoldersDetailsPage/hooks/useCartActions"

type SelectPassPageProps = {
  onBackNavigation: () => void
  onProceedToPassHoldersDetails: () => void
}

const SelectPassPage = ({
  onBackNavigation,
  onProceedToPassHoldersDetails,
}: SelectPassPageProps) => {
  const { t } = useTranslation()
  const isSummer = useIsSummer()
  const { categoryId } = useCategoryParam()
  const { bookingLengthId, setBookingLength: setBookingLengthParam } =
    useBookingLengthParam()
  const { startDate, setStartDate } = useDateParam()
  const { resortExternalId } = useResortParam()
  const { clearProducts, selectedProducts } = useSkipassStore()
  const hasAutoSelected = useRef(false)
  const userHasManuallySelectedBookingLengthRef = useRef(false)
  const dateHasChangedRef = useRef(false)
  const {
    selectedBookingLength,
    bookingLengths,
    isError: isBookingLengthsError,
    isLoading: isBookingLengthsLoading,
  } = useSelectedBookingLength()
  const {
    selectedResort,
    isLoading: isSelectedResortLoading,
    isError: isSelectedResortError,
  } = useSelectedResort()
  const { data: cartUsers, isFetched: isCartUsersFetched } = useCartUsersQuery({
    resortExternalId,
    startDate,
  })
  const hasSelectedProducts = selectedProducts.length > 0
  const { isLoading, error, addTicketsToCart } = useCartActions()
  const [validationError, setValidationError] = useState<string>()
  const { data: cartContext } = useCartContextQuery(resortExternalId)
  const isSeasonProductInCart = cartContext?.isSeasonProductInCart ?? false
  const hasServiceProductInCart = cartContext?.hasServiceProductInCart ?? false

  const viewItemSkipassToSkiersDetails = useDataLayer(
    "view_item_skipass_to_skiers_details"
  )

  const viewItemSkipassChooseDays = useDataLayer(
    "view_item_skipass_choose_days"
  )

  const {
    minDate,
    maxDate,
    unAvailableDates,
    isLoading: isDateConstraintsLoading,
  } = useDateConstraints(resortExternalId)

  const queryClient = useQueryClient()
  const [calendarViewMonth, setCalendarViewMonth] = useState(startDate)

  useEffect(() => {
    setCalendarViewMonth(startDate)
  }, [startDate])

  const { data: categoryUnavailableDates, isLoading: isCategoryDatesLoading } =
    useUnavailableDatesPerCategoryQuery({
      resortId: selectedResort?.id,
      categoryId,
      year: calendarViewMonth.getFullYear(),
      month: calendarViewMonth.getMonth() + 1,
    })

  const mergedUnavailableDates = useMemo(
    () => [...(unAvailableDates ?? []), ...(categoryUnavailableDates ?? [])],
    [unAvailableDates, categoryUnavailableDates]
  )

  const prefetchNextMonth = useCallback(
    (month: Date) => {
      const nextMonth = addMonths(month, 1)
      queryClient.prefetchQuery(
        unavailableDatesPerCategoryQueryOptions({
          resortId: selectedResort?.id,
          categoryId,
          year: nextMonth.getFullYear(),
          month: nextMonth.getMonth() + 1,
        })
      )
    },
    [queryClient, selectedResort?.id, categoryId]
  )

  const handleCalendarOpen = () => {
    prefetchNextMonth(calendarViewMonth)
  }

  const handleCalendarMonthChange = (newMonth: Date) => {
    setCalendarViewMonth(newMonth)
    prefetchNextMonth(newMonth)
  }

  const isTablet = useMediaQuery(theme.breakpoints.up("md"))

  const bookingLengthConfirmation = useConfirmBeforeAction(hasSelectedProducts)
  const backNavConfirmation = useConfirmBeforeAction(hasSelectedProducts)

  const {
    data: productsResponse,
    isError: isProductsError,
    isLoading: isProductsLoading,
  } = useProducts({
    resortId: selectedResort?.id,
    arrivalDate: startDate,
    encodedProductIds: selectedBookingLength?.encodedProductIds || "",
    categoryId,
  })

  const {
    getProductStatus,
    isProductSelectable,
    hasBlockedProducts,
    isLoading: isStatusLoading,
  } = useProductStatus({
    products: productsResponse?.products ?? [],
    capacityGroupIds: productsResponse?.capacityGroupIds ?? [],
    selectedProducts,
    isSeasonProductInCart,
    hasServiceProductInCart,
  })

  const isProductsReady = !isProductsLoading && productsResponse !== undefined
  usePrerenderSignal(isProductsReady)

  const handleBackNavigation = () => {
    backNavConfirmation.checkAndProceed(() => onBackNavigation())
  }

  const handleBookingLengthChange = (bookingLength: BookingLength) => {
    bookingLengthConfirmation.checkAndProceed(() => {
      userHasManuallySelectedBookingLengthRef.current = true
      setBookingLengthParam(bookingLength.id, { replace: true })
    })
  }

  const handleDateChange = (newDate: Date) => {
    bookingLengthConfirmation.checkAndProceed(() => {
      dateHasChangedRef.current = true
      setStartDate(newDate)
    })
  }

  const handleBookingLengthAnalytics = (bookingLength: BookingLength) => {
    viewItemSkipassChooseDays({
      Category: bookingLength.name,
      MinLength: bookingLength.minLength.toString(),
    })
  }

  useEffect(() => {
    const isCurrentSelectionValid = bookingLengths?.some(
      (bl) => bl.id === bookingLengthId
    )
    if (!bookingLengths?.length || !isCartUsersFetched) return
    if (
      isCurrentSelectionValid &&
      (!dateHasChangedRef.current ||
        userHasManuallySelectedBookingLengthRef.current)
    )
      return

    const autoSelectTarget = (() => {
      if (cartUsers?.numberOfDays) {
        const matchingLength = bookingLengths
          .filter((bl) => bl.minLength <= cartUsers.numberOfDays)
          .sort((a, b) => b.minLength - a.minLength)[0]
        if (matchingLength) return matchingLength
      }
      return bookingLengths[0]
    })()

    if (autoSelectTarget.id === bookingLengthId) return

    userHasManuallySelectedBookingLengthRef.current = false
    setBookingLengthParam(autoSelectTarget.id, { replace: true })
  }, [
    bookingLengths,
    bookingLengthId,
    cartUsers,
    isCartUsersFetched,
    setBookingLengthParam,
  ])

  const { data: categories } = useWebCategories(resortExternalId, startDate)
  const currentCategory = categories?.find((c) => c.id === categoryId)
  const isTicketCategory = currentCategory?.isTicketCategory ?? false

  const handleTicketProductSubmission = () => {
    const ticketIds = selectedProducts.flatMap((product) => {
      const quantity = product.quantity || 1
      return Array(quantity).fill(product.productPriceId)
    })

    addTicketsToCart(ticketIds)
  }

  const handlePriceSummeryButtonClick = () => {
    if (selectedProducts.length === 0) {
      setValidationError(t("totalPriceBar.noPassSelectedError"))
      return
    }
    setValidationError(undefined)
    if (isTicketCategory) {
      handleTicketProductSubmission()
    } else {
      onProceedToPassHoldersDetails()
    }
    viewItemSkipassToSkiersDetails({
      Category: selectedBookingLength?.name ?? null,
      MinLength: selectedBookingLength?.minLength?.toString() ?? null,
    })
  }

  const displayError = validationError || error

  useEffect(() => {
    if (selectedProducts.length > 0 && validationError) {
      setValidationError(undefined)
    }
  }, [selectedProducts, validationError])

  const renderProducts = (): JSX.Element => {
    if (!selectedBookingLength) {
      if (
        isBookingLengthsLoading ||
        !selectedResort ||
        categoryId === undefined
      ) {
        return <Skeleton variant="rectangular" height={450} width={"100%"} />
      }
      return (
        <Alert severity="info">{t("selectPassPage.noPassAvailable")}</Alert>
      )
    }

    if (isProductsLoading || selectedResort?.id === undefined) {
      return <Skeleton variant="rectangular" height={450} width={"100%"} />
    }

    if (isProductsError || productsResponse === undefined) {
      return <Alert severity="error">{t("productsFetchError")}</Alert>
    }

    if (productsResponse.products.length === 0) {
      return (
        <Alert severity="info">{t("selectPassPage.noPassAvailable")}</Alert>
      )
    }

    return (
      <ProductList
        products={productsResponse.products}
        productIds={productsResponse.productIds}
        arrivalDate={startDate}
        productTypeId={productsResponse.productTypeId}
        categoryId={categoryId!}
        bookingLengthName={selectedBookingLength?.name}
        cartUsers={cartUsers}
        hasAutoSelected={hasAutoSelected}
        getProductStatus={getProductStatus}
        isProductSelectable={isProductSelectable}
        isStatusLoading={isStatusLoading}
      />
    )
  }

  const isLoadingBookingLengths =
    isBookingLengthsLoading || !selectedResort || categoryId === undefined
  const showBookingLength =
    isLoadingBookingLengths ||
    isBookingLengthsError ||
    (bookingLengths && bookingLengths.length > 1)

  const selectPassHeadline = selectedResort?.name
    ? `${t("selectPassPage.pageTitle")} ${selectedResort.name}`
    : t("selectPassPage.pageTitle")

  if (isSelectedResortError) {
    return (
      <PageContainer
        headline={selectPassHeadline}
        headlineLoading={isSelectedResortLoading}
        leftIcon={<SkipassSolid />}
        onBackClick={onBackNavigation}
        activeStep={0}
        isSummer={isSummer}
      >
        <Alert severity="error" data-testid="data-fetch-error">
          {t("resortsFetchError")}
        </Alert>
      </PageContainer>
    )
  }

  return (
    <PageContainer
      headline={selectPassHeadline}
      headlineLoading={isSelectedResortLoading}
      leftIcon={<SkipassSolid />}
      onBackClick={handleBackNavigation}
      activeStep={0}
      isTicketProductType={isTicketCategory}
      isSummer={isSummer}
    >
      <ConfirmNavigationDialog
        open={bookingLengthConfirmation.showDialog}
        onClose={bookingLengthConfirmation.handleCancel}
        onSecondaryAction={() =>
          bookingLengthConfirmation.handleConfirm(clearProducts)
        }
        onPrimaryAction={() => {
          bookingLengthConfirmation.handleCancel()
          onProceedToPassHoldersDetails()
        }}
        headingText={t("confirmChangeBookingLengthDialog.heading", {
          context: isSummer ? "summer" : undefined,
        })}
        bodyText={t("confirmChangeBookingLengthDialog.body")}
        secondaryButtonText={t(
          "confirmChangeBookingLengthDialog.clearButtonLabel",
          { context: isSummer ? "summer" : undefined }
        )}
        primaryButtonText={t(
          "confirmChangeBookingLengthDialog.proceedToPassHolderDetails",
          { context: isSummer ? "summer" : undefined }
        )}
      />
      <ConfirmNavigationDialog
        open={backNavConfirmation.showDialog}
        onClose={backNavConfirmation.handleCancel}
        onSecondaryAction={backNavConfirmation.handleCancel}
        onPrimaryAction={() => backNavConfirmation.handleConfirm(clearProducts)}
        headingText={t("confirmBackNavigationDialog.heading", {
          context: isSummer ? "summer" : undefined,
        })}
        bodyText={t("confirmBackNavigationDialog.body", {
          context: isSummer ? "summer" : undefined,
        })}
        secondaryButtonText={t("confirmBackNavigationDialog.cancelButtonLabel")}
        primaryButtonText={t("confirmBackNavigationDialog.clearButtonLabel")}
      />
      <Stack spacing={2} mb={4}>
        <Card>
          <CardContent sx={{ padding: 2, "&:last-child": { pb: 0 } }}>
            <Stack spacing={0.5} mb={2}>
              <SummaryGrid>
                <SummaryRow
                  label={t("selectPassPage.destinationLabel")}
                  value={selectedResort?.name || ""}
                />
                {bookingLengths?.length === 1 && (
                  <SummaryRow
                    label={
                      isTicketCategory
                        ? t("selectActivityLabel")
                        : t("bookingLengthLabel")
                    }
                    value={bookingLengths[0].name}
                  />
                )}
              </SummaryGrid>
            </Stack>

            <Stack
              direction={isTablet ? "row" : "column"}
              spacing={isTablet ? 1.5 : 1}
              mb={1}
              sx={!showBookingLength ? { maxWidth: "20.5rem" } : undefined}
            >
              <DateSelector
                label={t("dateSelectLabel")}
                selectedDate={startDate}
                setSelectedDate={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                unAvailableDates={mergedUnavailableDates}
                isLoading={isDateConstraintsLoading}
                isLoadingDates={isCategoryDatesLoading}
                onCalendarOpen={handleCalendarOpen}
                onMonthChange={handleCalendarMonthChange}
              />
              {bookingLengths?.length !== 1 && (
                <PassDurationSelector
                  onSelect={handleBookingLengthChange}
                  onBookingLengthAnalytics={handleBookingLengthAnalytics}
                  selectedBookingLengthId={selectedBookingLength?.id || ""}
                  isLoading={isLoadingBookingLengths}
                  isError={isBookingLengthsError}
                  bookingLengths={bookingLengths || []}
                  isTicketCategory={isTicketCategory}
                />
              )}
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <PriceSummaryDrawer />
              <StyledHelperText
                helperText={t("selectPassPage.priceValidityMessage", {
                  date: format(new Date(), "yyyy-MM-dd"),
                })}
                severity="info"
              />
            </Stack>
          </CardContent>
        </Card>

        {hasBlockedProducts && (
          <Alert severity="warning">
            {isSeasonProductInCart
              ? t("selectPassPage.cartConflict.seasonInCart")
              : t("selectPassPage.cartConflict.otherInCart")}
          </Alert>
        )}

        <SrOnly>
          <h2 id="products-heading">{t("selectPassPage.productsHeading")}</h2>
        </SrOnly>
        <Grid
          container
          spacing={2}
          mb={4}
          role="group"
          aria-labelledby="products-heading"
        >
          {renderProducts()}
        </Grid>
        <FyiWidget page="selectPass" />
        {hasSelectedProducts && (
          <PriceSummeryBar
            buttonText={
              isTicketCategory
                ? t("totalPriceBar.addToCart")
                : t("totalPriceBar.proceedToPassHolderDetails", {
                    context: isSummer ? "summer" : undefined,
                  })
            }
            showCartIcon={isTicketCategory}
            isLoading={isLoading}
            error={displayError}
            onClick={handlePriceSummeryButtonClick}
            isSummer={isSummer}
          />
        )}
      </Stack>
    </PageContainer>
  )
}

export default SelectPassPage
